import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, ExternalLink, Copy, FileText, ChevronDown, ChevronUp, Download } from "lucide-react";
import { generateCanadaVisaPDF } from "@/lib/canadaVisaPdf";

const STATUS_LABELS: Record<string, string> = {
  new: "Шинэ",
  in_progress: "Бөглөж байгаа",
  submitted: "Илгээсэн",
  reviewing: "Шалгаж байгаа",
  info_needed: "Мэдээлэл дутуу",
  docs_needed: "Баримт дутуу",
  complete: "Материал бүрэн",
  ready: "Өргөдөл бэлэн",
  filed: "Эцэслэн илгээсэн",
  pending_decision: "Шийдвэр хүлээж байгаа",
  approved: "Зөвшөөрөгдсөн",
  rejected: "Татгалзсан",
};

const STATUS_COLORS: Record<string, string> = {
  new: "bg-gray-100 text-gray-700",
  in_progress: "bg-blue-100 text-blue-700",
  submitted: "bg-indigo-100 text-indigo-700",
  reviewing: "bg-yellow-100 text-yellow-700",
  info_needed: "bg-orange-100 text-orange-700",
  docs_needed: "bg-red-100 text-red-700",
  complete: "bg-teal-100 text-teal-700",
  ready: "bg-cyan-100 text-cyan-700",
  filed: "bg-purple-100 text-purple-700",
  pending_decision: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-200 text-red-800",
};

const DOC_LABELS: Record<string, string> = {
  passport: "Гадаад паспорт",
  irgenii_unmehleh: "Иргэний үнэмлэх",
  gerleltiin_gerchilgee: "Гэрлэлтийн гэрчилгээ",
  ajliin_todorkhiilolt: "Ажлын газрын тодорхойлолт",
  business_docs: "Бизнесийн баримт",
  nd_khulga: "НД хуулга",
  dans_khulga: "Дансны хуулга",
  khadgalamj: "Хадгаламж",
  ul_khodlokh: "Үл хөдлөх лавлагаа",
  busad_sankhuu: "Бусад санхүү",
  photo: "Цээж зураг",
};

export default function CanadaVisaAdminPage() {
  const { user, isAuthenticated } = useAuth();
  
  const utils = trpc.useUtils();

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ clientName: "", clientEmail: "", clientPhone: "" });
  const [createdLink, setCreatedLink] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const { data: applications, isLoading } = trpc.canadaVisa.list.useQuery({}, {
    enabled: isAuthenticated && user?.role === "admin",
  });

  const { data: selectedApp, isLoading: loadingDetail } = trpc.canadaVisa.getById.useQuery(
    { id: selectedId! },
    { enabled: selectedId !== null }
  );

  const createMutation = trpc.canadaVisa.create.useMutation({
    onSuccess: (data) => {
      const link = `${window.location.origin}/canada-visa/${data.token}`;
      setCreatedLink(link);
      utils.canadaVisa.list.invalidate();
      toast.success("Линк амжилттай үүсгэгдлээ");
    },
    onError: () => toast.error("Алдаа гарлаа"),
  });

  const updateStatusMutation = trpc.canadaVisa.updateStatus.useMutation({
    onSuccess: () => {
      utils.canadaVisa.list.invalidate();
      if (selectedId) utils.canadaVisa.getById.invalidate({ id: selectedId });
      toast.success("Төлөв шинэчлэгдлээ");
    },
    onError: () => toast.error("Алдаа гарлаа"),
  });

  const handleCreate = () => {
    createMutation.mutate({
      clientName: createForm.clientName || undefined,
      clientEmail: createForm.clientEmail || undefined,
      clientPhone: createForm.clientPhone || undefined,
    });
  };

  const copyLink = (link: string) => {
    navigator.clipboard.writeText(link);
    toast.success("Линк хуулагдлаа");
  };

  if (!isAuthenticated || user?.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Зөвхөн админ хандах боломжтой.</p>
      </div>
    );
  }

  const filtered = (applications ?? []).filter(app => {
    const matchStatus = statusFilter === "all" || app.status === statusFilter;
    const matchSearch = !search ||
      (app.clientName ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (app.clientEmail ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (app.clientPhone ?? "").includes(search);
    return matchStatus && matchSearch;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-indigo-800 text-white py-5 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">🍁 Канадын виз — Өргөдлийн удирдлага</h1>
            <p className="text-blue-200 text-sm mt-0.5">Нийт: {applications?.length ?? 0} өргөдөл</p>
          </div>
          <Button
            onClick={() => { setShowCreate(!showCreate); setCreatedLink(""); }}
            className="bg-white text-blue-900 hover:bg-blue-50"
          >
            <Plus className="w-4 h-4 mr-2" /> Шинэ линк үүсгэх
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Create form */}
        {showCreate && (
          <div className="bg-white rounded-xl border shadow-sm p-6 space-y-4">
            <h2 className="font-semibold text-lg">Шинэ өргөдлийн линк үүсгэх</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label>Үйлчлүүлэгчийн нэр</Label>
                <Input value={createForm.clientName} onChange={e => setCreateForm(p => ({ ...p, clientName: e.target.value }))} placeholder="Нэр" />
              </div>
              <div className="space-y-1">
                <Label>Имэйл</Label>
                <Input value={createForm.clientEmail} onChange={e => setCreateForm(p => ({ ...p, clientEmail: e.target.value }))} placeholder="email@example.com" type="email" />
              </div>
              <div className="space-y-1">
                <Label>Утас</Label>
                <Input value={createForm.clientPhone} onChange={e => setCreateForm(p => ({ ...p, clientPhone: e.target.value }))} placeholder="+976 ..." />
              </div>
            </div>
            <Button onClick={handleCreate} disabled={createMutation.isPending} className="bg-blue-700 hover:bg-blue-800">
              {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Линк үүсгэх
            </Button>
            {createdLink && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-green-800">Линк амжилттай үүсгэгдлээ:</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-white border rounded px-2 py-1 flex-1 break-all">{createdLink}</code>
                  <Button size="sm" variant="outline" onClick={() => copyLink(createdLink)}>
                    <Copy className="w-3 h-3" />
                  </Button>
                  <a href={createdLink} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline"><ExternalLink className="w-3 h-3" /></Button>
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            placeholder="Нэр, имэйл, утасаар хайх..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-xs"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Бүх төлөв" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Бүх төлөв</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Applications list */}
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">Өргөдөл олдсонгүй</div>
        ) : (
          <div className="space-y-3">
            {filtered.map(app => (
              <div key={app.id} className="bg-white rounded-xl border shadow-sm overflow-hidden">
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                  onClick={() => setSelectedId(selectedId === app.id ? null : app.id)}
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-medium">{app.clientName || "—"}</p>
                      <p className="text-sm text-gray-500">{app.clientEmail || "—"} · {app.clientPhone || "—"}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {new Date(app.createdAt).toLocaleDateString("mn-MN")}
                        {app.submittedAt && ` · Илгээсэн: ${new Date(app.submittedAt).toLocaleDateString("mn-MN")}`}
                      </p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[app.status] ?? "bg-gray-100"}`}>
                      {STATUS_LABELS[app.status] ?? app.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={`${window.location.origin}/canada-visa/${app.token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                    >
                      <Button size="sm" variant="outline" className="text-xs">
                        <ExternalLink className="w-3 h-3 mr-1" /> Линк
                      </Button>
                    </a>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={e => { e.stopPropagation(); copyLink(`${window.location.origin}/canada-visa/${app.token}`); }}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                    {selectedId === app.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>

                {/* Detail panel */}
                {selectedId === app.id && (
                  <div className="border-t p-4 space-y-4 bg-gray-50">
                    {loadingDetail ? (
                      <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
                    ) : selectedApp ? (
                      <>
                        {/* Status update */}
                        <div className="flex flex-wrap items-end gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Төлөв өөрчлөх</Label>
                            <Select
                              value={selectedApp.status}
                              onValueChange={v => updateStatusMutation.mutate({ id: selectedApp.id, status: v as typeof selectedApp.status })}
                            >
                              <SelectTrigger className="w-48 h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                                  <SelectItem key={k} value={k}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <AdminNoteEditor app={selectedApp} onSave={(note) => updateStatusMutation.mutate({ id: selectedApp.id, status: selectedApp.status, adminNote: note })} />
                        </div>

                        {/* Documents */}
                        {selectedApp.documents && selectedApp.documents.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Оруулсан файлууд ({selectedApp.documents.length}):</p>
                            <div className="flex flex-wrap gap-2">
                              {selectedApp.documents.map(doc => (
                                <a
                                  key={doc.id}
                                  href={doc.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 bg-white border rounded px-2 py-1 text-xs text-blue-700 hover:bg-blue-50"
                                >
                                  <FileText className="w-3 h-3" />
                                  <span className="max-w-[150px] truncate">{DOC_LABELS[doc.category] ?? doc.category}: {doc.fileName}</span>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Form data summary */}
                        {selectedApp.formData && selectedApp.formData !== "{}" && (
                          <FormDataSummary formData={selectedApp.formData} />
                        )}

                        {/* Admin note */}
                        {selectedApp.adminNote && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                            <p className="font-medium text-yellow-800 mb-1">Дотоод тэмдэглэл:</p>
                            <p className="text-yellow-700">{selectedApp.adminNote}</p>
                          </div>
                        )}

                        {/* Download PDF */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateCanadaVisaPDF(selectedApp)}
                          className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          PDF татах
                        </Button>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminNoteEditor({ app, onSave }: { app: { adminNote: string | null }, onSave: (note: string) => void }) {
  const [note, setNote] = useState(app.adminNote ?? "");
  return (
    <div className="space-y-1 flex-1 min-w-[200px]">
      <Label className="text-xs">Дотоод тэмдэглэл</Label>
      <div className="flex gap-2">
        <Textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={1}
          className="text-sm h-8 min-h-0 py-1 resize-none"
          placeholder="Тэмдэглэл..."
        />
        <Button size="sm" variant="outline" onClick={() => onSave(note)} className="text-xs whitespace-nowrap">
          Хадгалах
        </Button>
      </div>
    </div>
  );
}

function FormDataSummary({ formData }: { formData: string }) {
  const [expanded, setExpanded] = useState(false);
  let data: Record<string, unknown> = {};
  try { data = JSON.parse(formData); } catch {}

  const keyLabels: Record<string, string> = {
    fullNameMongolian: "Монгол нэр",
    lastNameLatin: "Овог (латин)",
    firstNameLatin: "Нэр (латин)",
    email: "Имэйл",
    phone: "Утас",
    dob: "Төрсөн огноо",
    citizenship: "Иргэншил",
    passportNumber: "Паспортын дугаар",
    passportExpiry: "Паспортын хугацаа",
    purpose: "Зорилго",
    arrivalDate: "Явах огноо",
    departureDate: "Буцах огноо",
    maritalStatus: "Гэр бүлийн байдал",
    monthlyIncome: "Сарын орлого",
  };

  const summaryKeys = Object.keys(keyLabels);
  const summaryEntries = summaryKeys.filter(k => data[k]).map(k => ({ label: keyLabels[k], value: String(data[k]) }));

  return (
    <div>
      <button
        className="text-sm font-medium text-blue-700 hover:text-blue-900 flex items-center gap-1"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        Маягтын мэдээлэл харах
      </button>
      {expanded && (
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {summaryEntries.map(e => (
            <div key={e.label} className="bg-white border rounded p-2 text-xs">
              <p className="text-gray-400">{e.label}</p>
              <p className="font-medium text-gray-800 truncate">{e.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
